import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import ReactSelect from 'react-select';
import itemService from '../../../services/ItemService';
import useFetchClassRefRecords from '../../MyTime/hooks/useFetchClassRefs';
import { useFetchQboAccounts } from '../../MyTime/hooks/useFetchQboAccounts';
import { useFetchTaxCode } from '../../MyTime/hooks/useFetchTaxCode';
import useFetchVendorRefRecords from '../../MyTime/hooks/useFetchVendorRefs';

const ITEM_TYPES = ['Service', 'Inventory', 'NonInventory', 'Category', 'Group'];

const toOption = (reference) => ({
  value: String(reference.value || reference.id || ''),
  label: reference.name || reference.label || reference.value || ''
});

const selectedOption = (options, reference) =>
  options.find((option) => String(option.value) === String(reference?.value || '')) || null;

const optionToRef = (option) => (option ? { value: String(option.value), name: option.label } : null);

const ReferenceField = ({ label, inputId, options, value, loading, onChange }) => (
  <div>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <ReactSelect
      inputId={inputId}
      aria-label={label}
      isClearable
      isSearchable
      isLoading={loading}
      options={options}
      value={selectedOption(options, value)}
      onChange={(option) => onChange(optionToRef(option))}
      // eslint-disable-next-line no-undef
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1400 }) }}
    />
  </div>
);

ReferenceField.propTypes = {
  label: PropTypes.string.isRequired,
  inputId: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  loading: PropTypes.bool,
  onChange: PropTypes.func.isRequired
};

const ItemDialogContent = ({ row, onChange, onCancelChanges, onApplyChanges }) => {
  const { classRefs, classRefLoading } = useFetchClassRefRecords();
  const { qboAccounts, qboAccountLoading } = useFetchQboAccounts();
  const { taxCode, taxCodeLoading } = useFetchTaxCode();
  const { vendorRefs, vendorRefLoading } = useFetchVendorRefRecords();
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await itemService.getItemsFromQBO({ take: 1000, skip: 0, type: 'Category' });
        const qboCategories = response?.data?.data?.QueryResponse?.Item || [];
        if (active) {
          setCategories(
            qboCategories
              .filter((item) => item.Active !== false)
              .map((item) => ({ value: String(item.Id), label: item.FullyQualifiedName || item.Name }))
          );
        }
      } catch {
        if (active) setCategories([]);
      } finally {
        if (active) setCategoriesLoading(false);
      }
    };
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  const accountOptions = useMemo(() => qboAccounts.map(toOption).filter((option) => option.value), [qboAccounts]);
  const classOptions = useMemo(() => classRefs.map(toOption).filter((option) => option.value), [classRefs]);
  const taxOptions = useMemo(() => taxCode.map(toOption).filter((option) => option.value), [taxCode]);
  const vendorOptions = useMemo(() => vendorRefs.map(toOption).filter((option) => option.value), [vendorRefs]);

  const changeValue = (name, value) => onChange({ target: { name, value } });
  const checkbox = (name, label) => (
    <FormControlLabel
      control={<Checkbox checked={Boolean(row[name])} onChange={(event) => changeValue(name, event.target.checked)} />}
      label={label}
    />
  );

  return (
    <React.Fragment>
      <DialogTitle id="form-dialog-title">Edit QuickBooks item</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f3f4f6' }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Basic info
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField label="Name" name="Name" value={row.Name ?? ''} onChange={onChange} required fullWidth />
            <TextField label="SKU" name="Sku" value={row.Sku ?? ''} onChange={onChange} fullWidth />
            <TextField
              select
              label="Item type"
              value={row.Type || 'Service'}
              onChange={(event) => changeValue('Type', event.target.value)}
              fullWidth
            >
              {ITEM_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Fully qualified name"
              value={row.FullyQualifiedName ?? ''}
              InputProps={{ readOnly: true }}
            />
            <ReferenceField
              label="Class"
              inputId="edit-item-class"
              options={classOptions}
              value={row.ClassRef}
              loading={classRefLoading}
              onChange={(value) => changeValue('ClassRef', value)}
            />
            <ReferenceField
              label="Parent category"
              inputId="edit-item-parent"
              options={categories}
              value={row.ParentRef}
              loading={categoriesLoading}
              onChange={(value) => changeValue('ParentRef', value)}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mt-2">
            {checkbox('Active', 'Active')}
            {checkbox('SubItem', 'Sub-item')}
          </div>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Sales
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Description"
              name="Description"
              value={row.Description ?? ''}
              onChange={onChange}
              multiline
              minRows={2}
              className="md:tw-col-span-2"
              fullWidth
            />
            <TextField
              label="Price/rate"
              name="UnitPrice"
              type="number"
              value={row.UnitPrice ?? ''}
              onChange={onChange}
              inputProps={{ step: '0.01' }}
              fullWidth
            />
            <ReferenceField
              label="Income account"
              inputId="edit-item-income-account"
              options={accountOptions}
              value={row.IncomeAccountRef}
              loading={qboAccountLoading}
              onChange={(value) => changeValue('IncomeAccountRef', value)}
            />
            <ReferenceField
              label="Sales tax code"
              inputId="edit-item-sales-tax"
              options={taxOptions}
              value={row.SalesTaxCodeRef}
              loading={taxCodeLoading}
              onChange={(value) => changeValue('SalesTaxCodeRef', value)}
            />
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mt-2">
            {checkbox('Taxable', 'Taxable')}
            {checkbox('SalesTaxIncluded', 'Sales tax included')}
          </div>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Purchasing
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Purchase description"
              name="PurchaseDesc"
              value={row.PurchaseDesc ?? ''}
              onChange={onChange}
              multiline
              minRows={2}
              className="md:tw-col-span-2"
              fullWidth
            />
            <TextField
              label="Purchase cost"
              name="PurchaseCost"
              type="number"
              value={row.PurchaseCost ?? ''}
              onChange={onChange}
              inputProps={{ step: '0.01' }}
              fullWidth
            />
            <ReferenceField
              label="Expense account"
              inputId="edit-item-expense-account"
              options={accountOptions}
              value={row.ExpenseAccountRef}
              loading={qboAccountLoading}
              onChange={(value) => changeValue('ExpenseAccountRef', value)}
            />
            <ReferenceField
              label="Preferred supplier"
              inputId="edit-item-vendor"
              options={vendorOptions}
              value={row.PrefVendorRef}
              loading={vendorRefLoading}
              onChange={(value) => changeValue('PrefVendorRef', value)}
            />
            <ReferenceField
              label="Purchase tax code"
              inputId="edit-item-purchase-tax"
              options={taxOptions}
              value={row.PurchaseTaxCodeRef}
              loading={taxCodeLoading}
              onChange={(value) => changeValue('PurchaseTaxCodeRef', value)}
            />
          </div>
          <div className="tw-mt-2">{checkbox('PurchaseTaxIncluded', 'Purchase tax included')}</div>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Inventory and QuickBooks details
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Quantity on hand"
              name="QtyOnHand"
              type="number"
              value={row.QtyOnHand ?? ''}
              onChange={onChange}
              fullWidth
            />
            <TextField
              label="Reorder point"
              name="ReorderPoint"
              type="number"
              value={row.ReorderPoint ?? ''}
              onChange={onChange}
              fullWidth
            />
            <TextField
              label="Inventory start date"
              name="InvStartDate"
              type="date"
              value={row.InvStartDate ? String(row.InvStartDate).slice(0, 10) : ''}
              onChange={onChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <ReferenceField
              label="Asset account"
              inputId="edit-item-asset-account"
              options={accountOptions}
              value={row.AssetAccountRef}
              loading={qboAccountLoading}
              onChange={(value) => changeValue('AssetAccountRef', value)}
            />
            <TextField label="QuickBooks item ID" value={row.Id || row.id || ''} InputProps={{ readOnly: true }} />
            <TextField label="Sync token" value={row.SyncToken ?? ''} InputProps={{ readOnly: true }} />
          </div>
          <div className="tw-mt-2">{checkbox('TrackQtyOnHand', 'Track quantity on hand')}</div>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancelChanges}>Cancel</Button>
        <Button onClick={onApplyChanges} variant="contained">
          Save
        </Button>
      </DialogActions>
    </React.Fragment>
  );
};

ItemDialogContent.propTypes = {
  row: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onCancelChanges: PropTypes.func.isRequired,
  onApplyChanges: PropTypes.func.isRequired
};

export default ItemDialogContent;
